import { Component, inject, OnInit, output, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ClientController, ClientEntity, OrderEntity } from '@domain';
import {
    RaButtonComponent,
    RaInputComponent,
    RaLoadingComponent,
    RaToastService,
} from '@shared/components';
import { debounceTime } from 'rxjs';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaFilletComponent } from 'src/app/shared/components/ra-fillet/ra-fillet.component';
import { AddressFormatPipe } from '../../../../../../shared/pipes/address-format.pipe';
import { OrderCreateService } from '../../services/order-create.service';

@Component({
    selector: 'order-list-clients',
    templateUrl: 'order-list-clients.component.html',
    styleUrl: 'order-list-clients.component.scss',
    imports: [
        RaLoadingComponent,
        RaEmptyComponent,
        RaInputComponent,
        RaFilletComponent,
        RaButtonComponent,
        AddressFormatPipe,
        TranslatePipe,
    ],
})
export class OrderListClientsComponent implements OnInit {
    public created = output<OrderEntity>();
    public loading = signal<boolean>(false);
    public loadingCreate = signal<boolean>(false);
    public clients = signal<ClientEntity[]>([]);
    public clientsFiltered = signal<ClientEntity[]>([]);
    public searchControl = new FormControl('');

    // * [Injects]
    private _raToastService = inject(RaToastService);
    private _translate = inject(TranslateService);
    public orderCreateService = inject(OrderCreateService);
    private _clientController = inject(ClientController);

    ngOnInit(): void {
        this._getClients();
        this._listenSearchControl();
    }

    private _getClients(): void {
        if (this.clients().length > 0 || this.loading()) return;

        this.loading.set(true);
        this._clientController.getAll().subscribe({
            next: clients => {
                this.clients.set(clients);
                this.clientsFiltered.set(clients);
                this.loading.set(false);
            },
            error: () => this.loading.set(false),
        });
    }

    public nextStep(): void {
        // Lista ainda carregando ou serviço já sendo criado: ignora o clique
        if (this.loading() || this.loadingCreate()) return;
        if (this.orderCreateService.client() == null) {
            this._raToastService.error({
                message: this._translate.instant('order.listClients.selectClientRequired'),
            });
            return;
        }

        this.loadingCreate.set(true);
        this.orderCreateService.create().subscribe({
            next: ({ order }) => {
                this.loadingCreate.set(false);

                this.created.emit(order);
            },
            error: () => this.loadingCreate.set(false),
        });
    }

    /**
     * Insere na lista o cliente recém-cadastrado e já o deixa selecionado.
     * A busca é limpa para que ele apareça mesmo se houver um filtro digitado.
     */
    public addClient(client: ClientEntity): void {
        this.clients.set([client, ...this.clients()]);
        this.searchControl.setValue('', { emitEvent: false });
        this.clientsFiltered.set(this.clients());
        this.orderCreateService.client.set(client);
    }

    private _filterClients(): void {
        const searchValue = this.searchControl.value?.toLowerCase().trim() || '';

        if (!searchValue) {
            this.clientsFiltered.set(this.clients());
            return;
        }

        this.clientsFiltered.set(
            this.clients().filter(client => client.name?.toLowerCase().includes(searchValue))
        );
    }

    private _listenSearchControl(): void {
        this.searchControl.valueChanges
            .pipe(debounceTime(600))
            .subscribe(() => this._filterClients());
    }
}
